// CAMINHO: src/components/DatajudSearchModal.tsx
// VERSÃO: FINAL COMPLETA E COM TODAS AS FUNCIONALIDADES

import { useState } from "react";
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Plus, RefreshCcw, Landmark, Users, GanttChartSquare, ExternalLink, AlertTriangle, Info, CheckCircle } from "lucide-react";
import type { ProcessData, ProcessMovement, ProcessPart } from "@/types/datajud";
import { getDatajudAlias, datajudSearchUrl } from '@/utils/datajudTribunal';

// LISTA COMPLETA E ORGANIZADA DE TRIBUNAIS
const justicaEstadual = [ { value: 'tjac', label: 'TJAC - Tribunal de Justiça do Acre' }, { value: 'tjal', label: 'TJAL - Tribunal de Justiça de Alagoas' }, { value: 'tjap', label: 'TJAP - Tribunal de Justiça do Amapá' }, { value: 'tjam', label: 'TJAM - Tribunal de Justiça do Amazonas' }, { value: 'tjba', label: 'TJBA - Tribunal de Justiça da Bahia' }, { value: 'tjce', label: 'TJCE - Tribunal de Justiça do Ceará' }, { value: 'tjdft', label: 'TJDFT - TJ do Distrito Federal e Territórios' }, { value: 'tjes', label: 'TJES - Tribunal de Justiça do Espírito Santo' }, { value: 'tjgo', label: 'TJGO - Tribunal de Justiça de Goiás' }, { value: 'tjma', label: 'TJMA - Tribunal de Justiça do Maranhão' }, { value: 'tjmt', label: 'TJMT - Tribunal de Justiça do Mato Grosso' }, { value: 'tjms', label: 'TJMS - Tribunal de Justiça do Mato Grosso do Sul' }, { value: 'tjmg', label: 'TJMG - Tribunal de Justiça de Minas Gerais' }, { value: 'tjpa', label: 'TJPA - Tribunal de Justiça do Pará' }, { value: 'tjpb', label: 'TJPB - Tribunal de Justiça da Paraíba' }, { value: 'tjpr', label: 'TJPR - Tribunal de Justiça do Paraná' }, { value: 'tjpe', label: 'TJPE - Tribunal de Justiça de Pernambuco' }, { value: 'tjpi', label: 'TJPI - Piauí' }, { value: 'tjrj', label: 'TJRJ - Tribunal de Justiça do Rio de Janeiro' }, { value: 'tjrn', label: 'TJRN - Tribunal de Justiça do Rio Grande do Norte' }, { value: 'tjrs', label: 'TJRS - Tribunal de Justiça do Rio Grande do Sul' }, { value: 'tjro', label: 'TJRO - Tribunal de Justiça de Rondônia' }, { value: 'tjrr', label: 'TJRR - Tribunal de Justiça de Roraima' }, { value: 'tjsc', label: 'TJSC - Tribunal de Justiça de Santa Catarina' }, { value: 'tjsp', label: 'TJSP - Tribunal de Justiça de São Paulo' }, { value: 'tjse', label: 'TJSE - Tribunal de Justiça de Sergipe' }, { value: 'tjto', label: 'TJTO - Tribunal de Justiça do Tocantins' }];
const justicaFederal = [ { value: 'trf1', label: 'TRF1 - Tribunal Regional Federal da 1ª Região' }, { value: 'trf2', label: 'TRF2 - Tribunal Regional Federal da 2ª Região' }, { value: 'trf3', label: 'TRF3 - Tribunal Regional Federal da 3ª Região' }, { value: 'trf4', label: 'TRF4 - Tribunal Regional Federal da 4ª Região' }, { value: 'trf5', label: 'TRF5 - Tribunal Regional Federal da 5ª Região' }, { value: 'trf6', label: 'TRF6 - Tribunal Regional Federal da 6ª Região' }];

// FUNÇÃO INTELIGENTE PARA GERAR LINKS PÚBLICOS
const getPublicUrlForProcess = (proc: ProcessData): string => {
    const numeroLimpo = proc.numeroProcesso.replace(/\D/g, '');
    // O link de busca do Google é a forma mais robusta e universal
    return `https://www.google.com/search?q=${numeroLimpo}+${proc.tribunal}`;
}

// FUNÇÃO PARA DETERMINAR TRIBUNAL PELO NÚMERO DO PROCESSO
const getTribunalFromProcessNumber = (numeroProcesso: string): string => getDatajudAlias(numeroProcesso) || 'tjpe';

// SUB-COMPONENTE PARA EXIBIR OS DETALHES DO RESULTADO
const SearchResultDetail = ({ proc }: { proc: ProcessData }) => {
  return (
    <Tabs defaultValue="geral" className="w-full">
      <TabsList className="grid w-full grid-cols-3">
        <TabsTrigger value="geral">Dados Gerais</TabsTrigger>
        <TabsTrigger value="partes">Partes Envolvidas</TabsTrigger>
        <TabsTrigger value="andamentos">Andamentos</TabsTrigger>
      </TabsList>
      <TabsContent value="geral" className="mt-4 space-y-4">
        <Card>
          <CardHeader><CardTitle className="text-base">Informações Principais do Processo</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div><p className="font-medium text-muted-foreground">Número CNJ</p><p>{proc.numeroProcesso}</p></div>
            <div><p className="font-medium text-muted-foreground">Tribunal</p><p>{proc.tribunal}</p></div>
            <div className="col-span-2"><p className="font-medium text-muted-foreground">Órgão Julgador</p><p>{proc.orgaoJulgador?.nome || 'Não informado'}</p></div>
            <div><p className="font-medium text-muted-foreground">Classe</p><p>{proc.classe?.nome || 'Não informada'}</p></div>
            <div><p className="font-medium text-muted-foreground">Data de Autuação</p><p>{proc.dataAjuizamento ? format(new Date(proc.dataAjuizamento), "dd/MM/yyyy", { locale: ptBR }) : 'N/I'}</p></div>
            <div className="col-span-2"><p className="font-medium text-muted-foreground">Assunto Principal</p><p>{(proc.assunto && proc.assunto.length > 0) ? proc.assunto[0].nome : 'Não especificado'}</p></div>
          </CardContent>
        </Card>
      </TabsContent>
      <TabsContent value="partes" className="mt-4">
        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2 text-base"><Users className="w-5 h-5"/>Partes do Processo</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h4 className="font-semibold mb-2 border-b pb-1">Polo Ativo</h4>
              {proc.partes && proc.partes.length > 0 ? (proc.partes.filter(p => p.polo === 'ATIVO').map((parte, index) => (<div key={index} className="text-sm py-1"><p>{parte.nome}</p><p className="text-xs text-muted-foreground">{parte.tipo}</p></div>))) : <p className="text-sm text-muted-foreground">Nenhuma parte encontrada no polo ativo.</p>}
            </div>
            <div>
              <h4 className="font-semibold mb-2 border-b pb-1">Polo Passivo</h4>
              {proc.partes && proc.partes.length > 0 ? (proc.partes.filter(p => p.polo === 'PASSIVO').map((parte, index) => (<div key={index} className="text-sm py-1"><p>{parte.nome}</p><p className="text-xs text-muted-foreground">{parte.tipo}</p></div>))) : <p className="text-sm text-muted-foreground">Nenhuma parte encontrada no polo passivo.</p>}
            </div>
          </CardContent>
        </Card>
      </TabsContent>
      <TabsContent value="andamentos" className="mt-4">
        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2 text-base"><GanttChartSquare className="w-5 h-5"/>Movimentações</CardTitle></CardHeader>
          <CardContent className="space-y-3 max-h-[calc(90vh-400px)] overflow-y-auto">
            {proc.movimentos && proc.movimentos.length > 0 ? (proc.movimentos.map((mov, index) => (<div key={index} className="text-sm border-b pb-2 last:border-b-0">
              <div className="flex justify-between items-start"><p className="font-semibold flex-1 pr-4">{mov.nome}</p><a href={getPublicUrlForProcess(proc)} target="_blank" rel="noopener noreferrer"><Button variant="ghost" size="sm" className="h-7 text-xs"><ExternalLink className="w-3 h-3 mr-1" />Ver no Tribunal</Button></a></div>
              <p className="text-xs text-muted-foreground">{format(new Date(mov.dataHora), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}</p>
              {mov.complemento && <p className="text-sm mt-1 bg-slate-50 p-2 rounded border">{mov.complemento}</p>}
            </div>))) : <p className="text-sm text-muted-foreground">Nenhuma movimentação encontrada.</p>}
          </CardContent>
        </Card>
      </TabsContent>
    </Tabs>
  );
};

// COMPONENTE PARA LISTA DE RESULTADOS MÚLTIPLOS
const SearchResultsList = ({ 
  results, 
  selectedProcess, 
  onSelectProcess, 
  onProcessImport, 
  onProcessMonitor 
}: { 
  results: ProcessData[];
  selectedProcess: ProcessData | null;
  onSelectProcess: (proc: ProcessData) => void;
  onProcessImport: (proc: ProcessData) => void;
  onProcessMonitor: (proc: ProcessData) => void;
}) => {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Resultados Encontrados ({results.length})</h3>
        <Button 
          variant="outline" 
          size="sm"
          onClick={() => onSelectProcess(null)}
          disabled={!selectedProcess}
        >
          Voltar
        </Button>
      </div>
      
      <div className="space-y-3">
        {results.map((proc, index) => (
          <Card 
            key={index} 
            className={`cursor-pointer transition-all hover:shadow-md ${
              selectedProcess?.numeroProcesso === proc.numeroProcesso ? 'ring-2 ring-primary' : ''
            }`}
            onClick={() => onSelectProcess(proc)}
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h4 className="font-semibold">{proc.numeroProcesso}</h4>
                    {selectedProcess?.numeroProcesso === proc.numeroProcesso && (
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Tribunal: {proc.tribunal}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Classe: {proc.classe?.nome || 'Não informada'}
                  </p>
                  {proc.dataAjuizamento && (
                    <p className="text-sm text-muted-foreground">
                      Data: {format(new Date(proc.dataAjuizamento), "dd/MM/yyyy", { locale: ptBR })}
                    </p>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={(e) => {
                      e.stopPropagation();
                      onProcessImport(proc);
                    }}
                  >
                    Importar
                  </Button>
                  <Button 
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      onProcessMonitor(proc);
                    }}
                  >
                    <RefreshCcw className="w-3 h-3 mr-1" />
                    Monitorar
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

interface DatajudSearchModalProps {
  onProcessImport: (processo: ProcessData) => void;
  onProcessMonitor: (processo: ProcessData) => void;
  /** Botão só com ícone (barra de ferramentas da página Processos) */
  iconOnly?: boolean;
}

export function DatajudSearchModal({ onProcessImport, onProcessMonitor, iconOnly = false }: DatajudSearchModalProps) {
  const { toast } = useToast();
  const [search, setSearch] = useState("");
  const [selectedTribunal, setSelectedTribunal] = useState("tjpb");
  const [results, setResults] = useState<ProcessData[]>([]);
  const [loading, setLoading] = useState(false);
  const [classe, setClasse] = useState("");
  const [orgaoJulgador, setOrgaoJulgador] = useState("");
  const [autoDetectTribunal, setAutoDetectTribunal] = useState(true);
  const [selectedProcess, setSelectedProcess] = useState<ProcessData | null>(null);

  async function handleSearch() {
    setLoading(true);
    setResults([]);
    setSelectedProcess(null);
    
    if (!search && !classe && !orgaoJulgador) {
      toast({ title: "Atenção", description: "Preencha ao menos um campo de busca." });
      setLoading(false);
      return;
    }

    let tribunalToUse = selectedTribunal;
    
    // Auto-detectar tribunal se habilitado e número de processo fornecido
    if (autoDetectTribunal && search) {
      const detectedTribunal = getTribunalFromProcessNumber(search);
      tribunalToUse = detectedTribunal;
    }

    // PRIMEIRO: Testar se a API route está funcionando
    try {
      console.log('🧪 Testando API route...');
      const testResponse = await fetch('/api-datajud/test', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (testResponse.ok) {
        const testData = await testResponse.json();
        console.log('✅ API route funcionando:', testData);
      } else {
        console.error('❌ API route não está funcionando:', testResponse.status);
        toast({ title: "Erro", description: "API route não está funcionando", variant: "destructive" });
        setLoading(false);
        return;
      }
    } catch (testError) {
      console.error('❌ Erro ao testar API route:', testError);
      toast({ title: "Erro", description: "Erro ao testar API route", variant: "destructive" });
      setLoading(false);
      return;
    }

    // Construir query string para GET
    const queryParams = [];
    if (search) queryParams.push(`numeroProcesso:${search.replace(/\D/g, '')}`);
    if (classe) queryParams.push(`classe.codigo:${classe}`);
    if (orgaoJulgador) queryParams.push(`orgaoJulgador.codigo:${orgaoJulgador}`);
    
    const apiKey = 'APIKey cDZHYzlZa0JadVREZDJCendQbXY6SkJlTzNjLV9TRENyQk1RdnFKZGRQdw==';
    const queryString = queryParams.join(' AND ');
    
    // PRIMEIRA TENTATIVA: API route da Vercel
    try {
      console.log('🔄 Tentativa 1: API route da Vercel');
      const url = datajudSearchUrl(tribunalToUse, queryString);
      console.log('URL da API route:', url);
      
      const res = await fetch(url, { 
        method: 'GET', 
        headers: { 
          'Authorization': apiKey, 
          'Content-Type': 'application/json' 
        } 
      });

      console.log('Status da API route:', res.status);
      console.log('Headers da resposta:', Object.fromEntries(res.headers.entries()));

      if (!res.ok) {
        throw new Error(`API route falhou: ${res.status} - ${res.statusText}`);
      }

      const responseText = await res.text();
      console.log('Resposta bruta da API route (primeiros 200 chars):', responseText.substring(0, 200));
      
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error('❌ Erro ao fazer parse da resposta da API route:', parseError);
        throw new Error(`Resposta inválida da API route: ${responseText.substring(0, 100)}`);
      }

      const searchResults = data?.hits?.hits || [];
      const mappedResults: ProcessData[] = searchResults.map((hit: any) => hit._source);
      setResults(mappedResults);

      if (searchResults.length === 0) {
        toast({ title: "Nenhum resultado", description: "Verifique os critérios de busca." });
      } else if (searchResults.length === 1) {
        setSelectedProcess(mappedResults[0]);
        toast({ title: "Processo encontrado", description: "Processo encontrado com sucesso." });
      } else {
        toast({ title: "Múltiplos resultados", description: `${searchResults.length} processos encontrados. Selecione um para ver os detalhes.` });
      }
      
      console.log('✅ API route funcionou perfeitamente!');
      return;
      
    } catch (apiRouteError: any) {
      console.error('❌ API route falhou:', apiRouteError);
      
      // SEGUNDA TENTATIVA: Requisição direta para o DataJud
      try {
        console.log('🔄 Tentativa 2: Requisição direta para o DataJud');
        const directUrl = `https://api-publica.datajud.cnj.jus.br/api_publica_${tribunalToUse}/_search?q=${queryString}`;
        console.log('URL direta:', directUrl);
        
        const directRes = await fetch(directUrl, { 
          method: 'GET', 
          headers: { 
            'Authorization': apiKey, 
            'Content-Type': 'application/json' 
          } 
        });

        console.log('Status da requisição direta:', directRes.status);
        console.log('Headers da resposta direta:', Object.fromEntries(directRes.headers.entries()));

        if (!directRes.ok) {
          if (directRes.status === 401) {
            throw new Error("A chave de API é inválida ou expirou.");
          }
          if (directRes.status === 404) {
            throw new Error(`Tribunal ${tribunalToUse} não encontrado ou indisponível.`);
          }
          const errorText = await directRes.text();
          throw new Error(`Erro direto ${directRes.status}: ${errorText}`);
        }

        const directResponseText = await directRes.text();
        console.log('Resposta bruta direta (primeiros 200 chars):', directResponseText.substring(0, 200));
        
        let directData;
        try {
          directData = JSON.parse(directResponseText);
        } catch (directParseError) {
          console.error('❌ Erro ao fazer parse da resposta direta:', directParseError);
          throw new Error(`Resposta inválida direta: ${directResponseText.substring(0, 100)}`);
        }

        const directSearchResults = directData?.hits?.hits || [];
        const directMappedResults: ProcessData[] = directSearchResults.map((hit: any) => hit._source);
        setResults(directMappedResults);

        if (directSearchResults.length === 0) {
          toast({ title: "Nenhum resultado", description: "Verifique os critérios de busca." });
        } else if (directSearchResults.length === 1) {
          setSelectedProcess(directMappedResults[0]);
          toast({ title: "Processo encontrado (via direta)", description: "Processo encontrado com sucesso via conexão direta." });
        } else {
          toast({ title: "Múltiplos resultados (via direta)", description: `${directSearchResults.length} processos encontrados via conexão direta.` });
        }
        
        console.log('✅ Requisição direta funcionou!');
        return;
        
      } catch (directError: any) {
        console.error('❌ Requisição direta também falhou:', directError);
        throw new Error(`Ambas as tentativas falharam. API route: ${apiRouteError.message}. Direta: ${directError.message}`);
      }
    } finally {
      setLoading(false);
    }
  }

  const handleOpenChange = (isOpen: boolean) => { 
    if (!isOpen) { 
      setResults([]); 
      setSearch(''); 
      setClasse(''); 
      setOrgaoJulgador(''); 
      setSelectedProcess(null);
    }
  };

  const handleProcessSelect = (proc: ProcessData | null) => {
    setSelectedProcess(proc);
  };

  return (
    <Dialog onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        {iconOnly ? (
          <button type="button" className="flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 transition-colors hover:bg-[#faf7f2] hover:text-[#7a6440]" title="Consultar Datajud" aria-label="Consultar Datajud"><Landmark className="h-4 w-4" /></button>
        ) : (
          <Button variant="outline"><Plus className="w-4 h-4 mr-2" />Consultar DataJud</Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-md w-full h-[90vh] flex flex-col" aria-describedby="datajud-description">
        <DialogHeader>
          <DialogTitle>Busca Avançada no Datajud</DialogTitle>
          <DialogDescription id="datajud-description">
            Consulte processos nos tribunais brasileiros através do sistema Datajud
          </DialogDescription>
        </DialogHeader>
        
        {/* Informações do Processo Encontrado */}
        {selectedProcess && (
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4 mb-4">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-blue-900 mb-1">
                  Processo Encontrado
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="font-medium text-blue-800">Número:</span>
                    <span className="ml-2 text-blue-700">{selectedProcess.numeroProcesso}</span>
                  </div>
                  <div>
                    <span className="font-medium text-blue-800">Tribunal:</span>
                    <span className="ml-2 text-blue-700">{selectedProcess.tribunal}</span>
                  </div>
                  {selectedProcess.classe?.nome && (
                    <div>
                      <span className="font-medium text-blue-800">Classe:</span>
                      <span className="ml-2 text-blue-700">{selectedProcess.classe.nome}</span>
                    </div>
                  )}
                  {selectedProcess.orgaoJulgador?.nome && (
                    <div>
                      <span className="font-medium text-blue-800">Órgão:</span>
                      <span className="ml-2 text-blue-700">{selectedProcess.orgaoJulgador.nome}</span>
                    </div>
                  )}
                </div>
              </div>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setSelectedProcess(null)}
                className="text-blue-700 border-blue-300 hover:bg-blue-100"
              >
                Nova Busca
              </Button>
            </div>
          </div>
        )}
        
        {/* Formulário de Busca - Retraído quando há resultados */}
        <div className={`transition-all duration-300 ease-in-out ${
          selectedProcess ? 'max-h-0 overflow-hidden opacity-0' : 'max-h-96 opacity-100'
        }`}>
          <div className="space-y-4 p-1 border-b pb-4">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="autoDetect"
                checked={autoDetectTribunal}
                onChange={(e) => setAutoDetectTribunal(e.target.checked)}
                className="rounded"
              />
              <label htmlFor="autoDetect" className="text-sm">
                Auto-detectar tribunal pelo número do processo
              </label>
            </div>
            <div>
              <label className="text-sm font-medium">Tribunal</label>
              <Select value={selectedTribunal} onValueChange={setSelectedTribunal} disabled={autoDetectTribunal}>
                <SelectTrigger><SelectValue placeholder="Selecione um Tribunal" /></SelectTrigger>
                <SelectContent>
                  <SelectGroup><SelectLabel>Justiça Estadual</SelectLabel>{justicaEstadual.map(t => (<SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>))}</SelectGroup>
                  <SelectGroup><SelectLabel>Justiça Federal</SelectLabel>{justicaFederal.map(t => (<SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>))}</SelectGroup>
                </SelectContent>
              </Select>
            </div>
            <div><label className="text-sm font-medium">Número do Processo</label><Input placeholder="Somente números ou formatado..." value={search} onChange={e => setSearch(e.target.value)} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className="text-sm font-medium">Cód. da Classe</label><Input placeholder="Ex: 1116" value={classe} onChange={e => setClasse(e.target.value)} /></div>
              <div><label className="text-sm font-medium">Cód. do Órgão Julgador</label><Input placeholder="Ex: 13597" value={orgaoJulgador} onChange={e => setOrgaoJulgador(e.target.value)} /></div>
            </div>
            <Button onClick={handleSearch} disabled={loading} className="w-full">
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Buscar
            </Button>
          </div>
        </div>
        
        {/* Área de Resultados - Expandida quando há resultados */}
        <div className={`flex-1 transition-all duration-300 ease-in-out ${
          selectedProcess ? 'min-h-0' : 'min-h-96'
        }`}>
          <div className="h-full overflow-y-auto p-1">
            {loading && <div className="flex justify-center items-center h-full"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>}
            {!loading && results.length === 0 && !selectedProcess && (<div className="text-center text-gray-500 pt-16">Aguardando busca...</div>)}
            {!loading && results.length > 0 && !selectedProcess && (
              <SearchResultsList 
                results={results}
                selectedProcess={selectedProcess}
                onSelectProcess={handleProcessSelect}
                onProcessImport={onProcessImport}
                onProcessMonitor={onProcessMonitor}
              />
            )}
            {!loading && selectedProcess && (
              <div className="h-full">
                <SearchResultDetail proc={selectedProcess} />
              </div>
            )}
          </div>
        </div>
        
        {/* Botões de Ação - Só aparecem quando há processo selecionado */}
        {!loading && selectedProcess && (
          <DialogFooter className="p-4 border-t mt-auto bg-gradient-to-r from-blue-50 to-indigo-50">
            <Button variant="outline" onClick={() => onProcessImport(selectedProcess)}>
              Importar e Editar
            </Button>
            <Button onClick={() => onProcessMonitor(selectedProcess)}>
              <RefreshCcw className="w-4 h-4 mr-2" />
              Salvar e Monitorar
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}